import { useDialog } from '@theme/use-dialog'
import { isPointWithinElement } from '@theme/utils'

const MegaMenuConfig = {
  attributes: {
    trigger: 'trigger'
  },
  classes: {
    headerActive: 'mega-menu-active',
    overlay: 'mega-menu-overlay',
    overlayActive: 'is-active'
  },
  delays: {
    hover: 200
  },
  events: {
    documentMouseMove: 'mousemove',
    mouseEnter: 'mouseenter',
    mouseLeave: 'mouseleave',
    mouseMove: 'mousemove',
    scroll: 'scroll'
  },
  selectors: {
    header: 'sticky-header',
    headerSection: '.header',
    inner: '.mega-menu__inner'
  },
  vars: {
    top: '--mega-menu-top'
  }
}

class MegaMenu extends useDialog {
  timeout = null
  isOpening = false
  trigger = null
  triggerLink = null
  headerSection = null
  headerElement = null
  overlayElement = null
  isEditorMode = false
  positionFrame = null
  lastTopPosition = null
  headerResizeObserver = null

  setHeaderActive = (isActive) => {
    this.headerSection?.classList.toggle(
      MegaMenuConfig.classes.headerActive,
      isActive
    )
    this.headerElement?.classList.toggle(
      MegaMenuConfig.classes.headerActive,
      isActive
    )
  }

  setOverlayActive = (isActive) => {
    if (isActive && !this.overlayElement) {
      this.overlayElement = this.createOverlayElement()
    }

    this.overlayElement?.classList.toggle(
      MegaMenuConfig.classes.overlayActive,
      isActive
    )
  }

  createOverlayElement = () => {
    const overlay = document.createElement('div')
    overlay.className = MegaMenuConfig.classes.overlay
    document.body.append(overlay)
    return overlay
  }

  removeOverlayElement = () => {
    this.overlayElement?.remove()
    this.overlayElement = null
  }

  setTriggerActive = (isActive) => {
    if (!this.triggerLink) return

    this.triggerLink.classList.toggle('alt', isActive)
    this.triggerLink.setAttribute('aria-expanded', isActive ? 'true' : 'false')
  }

  cancelClose = () => {
    clearTimeout(this.timeout)
    this.timeout = null
  }

  scheduleClose = () => {
    if (this.isOpening || this.isEditorMode || this.timeout) return

    this.timeout = setTimeout(() => {
      this.timeout = null

      if (this.refs.dialog?.open) {
        this.closeDialog()
      }
    }, MegaMenuConfig.delays.hover)
  }

  isInsideInteractiveArea = (x, y) => {
    const menuInner = this.refs.dialog?.querySelector(
      MegaMenuConfig.selectors.inner
    )

    const insideTrigger =
      this.trigger && isPointWithinElement(x, y, this.trigger)
    const insideMenu = menuInner && isPointWithinElement(x, y, menuInner)

    return Boolean(insideTrigger || insideMenu)
  }

  updatePosition = () => {
    if (!(this.refs.dialog && this.headerElement)) return

    const headerRect = this.headerElement.getBoundingClientRect()
    const topPosition = Math.max(0, headerRect.bottom)

    if (
      this.lastTopPosition !== null &&
      Math.abs(this.lastTopPosition - topPosition) < 0.5
    ) {
      return
    }

    this.lastTopPosition = topPosition
    this.refs.dialog.style.setProperty(
      MegaMenuConfig.vars.top,
      `${topPosition.toFixed(2)}px`
    )
  }

  scheduleUpdatePosition = () => {
    if (this.positionFrame !== null) return

    this.positionFrame = requestAnimationFrame(() => {
      this.positionFrame = null
      this.updatePosition()
    })
  }

  onMouseEnter = async () => {
    if (this.isEditorMode) return
    this.cancelClose()

    if (this.refs.dialog && !this.refs.dialog.open) {
      this.isOpening = true
      this.setTriggerActive(true)
      this.setHeaderActive(true)
      this.setOverlayActive(true)
      this.updatePosition()
      await this.showDialog()
      this.updatePosition()
      this.isOpening = false
    }
  }

  onMouseLeave = () => {
    this.scheduleClose()
  }

  onScroll = () => {
    if (this.refs.dialog?.open) {
      this.scheduleUpdatePosition()
    }
  }

  onResize = () => {
    if (this.refs.dialog?.open) {
      this.scheduleUpdatePosition()
    }
  }

  onHeaderResize = () => {
    if (this.refs.dialog?.open) {
      this.scheduleUpdatePosition()
    }
  }

  onMouseMove = (event) => {
    if (!this.refs.dialog?.open || this.isOpening || this.isEditorMode) return

    if (this.isInsideInteractiveArea(event.clientX, event.clientY)) {
      this.cancelClose()
      return
    }

    this.scheduleClose()
  }

  onDocumentMouseMove = (event) => {
    if (!this.refs.dialog?.open || this.isOpening || this.isEditorMode) return

    if (this.isInsideInteractiveArea(event.clientX, event.clientY)) {
      this.cancelClose()
      return
    }

    this.scheduleClose()
  }

  onDialogClose = () => {
    this.setTriggerActive(false)
    this.setHeaderActive(false)
    this.setOverlayActive(false)
  }

  connectedCallback() {
    super.connectedCallback()

    const triggerSelector = this.getAttribute(MegaMenuConfig.attributes.trigger)
    this.trigger = triggerSelector
      ? document.querySelector(triggerSelector)
      : this.parentElement
    this.triggerLink = this.trigger?.matches?.('.link')
      ? this.trigger
      : this.trigger?.querySelector?.('.link')
    this.headerElement = document.querySelector(MegaMenuConfig.selectors.header)
    this.headerSection = this.headerElement?.closest(
      MegaMenuConfig.selectors.headerSection
    )
    this.setTriggerActive(Boolean(this.refs.dialog?.open))
    this.setHeaderActive(Boolean(this.refs.dialog?.open))
    this.setOverlayActive(Boolean(this.refs.dialog?.open))

    if (this.trigger) {
      this.trigger.addEventListener(
        MegaMenuConfig.events.mouseEnter,
        this.onMouseEnter
      )
      this.trigger.addEventListener(
        MegaMenuConfig.events.mouseLeave,
        this.onMouseLeave
      )
    }

    if (this.refs.dialog) {
      this.refs.dialog.addEventListener(
        MegaMenuConfig.events.mouseEnter,
        this.onMouseEnter
      )
      this.refs.dialog.addEventListener(
        MegaMenuConfig.events.mouseLeave,
        this.onMouseLeave
      )
      this.refs.dialog.addEventListener(
        MegaMenuConfig.events.mouseMove,
        this.onMouseMove
      )
      this.refs.dialog.addEventListener('close', this.onDialogClose)
    }

    document.addEventListener(
      MegaMenuConfig.events.documentMouseMove,
      this.onDocumentMouseMove
    )

    window.addEventListener(MegaMenuConfig.events.scroll, this.onScroll, {
      passive: true
    })

    window.addEventListener('resize', this.onResize, { passive: true })

    if (typeof ResizeObserver !== 'undefined' && this.headerElement) {
      this.headerResizeObserver = new ResizeObserver(this.onHeaderResize)
      this.headerResizeObserver.observe(this.headerElement)
    }
  }

  disconnectedCallback() {
    this.cancelClose()

    if (this.trigger) {
      this.trigger.removeEventListener(
        MegaMenuConfig.events.mouseEnter,
        this.onMouseEnter
      )
      this.trigger.removeEventListener(
        MegaMenuConfig.events.mouseLeave,
        this.onMouseLeave
      )
    }

    if (this.refs.dialog) {
      this.refs.dialog.removeEventListener(
        MegaMenuConfig.events.mouseEnter,
        this.onMouseEnter
      )
      this.refs.dialog.removeEventListener(
        MegaMenuConfig.events.mouseLeave,
        this.onMouseLeave
      )
      this.refs.dialog.removeEventListener(
        MegaMenuConfig.events.mouseMove,
        this.onMouseMove
      )
      this.refs.dialog.removeEventListener('close', this.onDialogClose)
    }

    document.removeEventListener(
      MegaMenuConfig.events.documentMouseMove,
      this.onDocumentMouseMove
    )

    window.removeEventListener(MegaMenuConfig.events.scroll, this.onScroll)
    window.removeEventListener('resize', this.onResize)

    if (this.headerResizeObserver) {
      this.headerResizeObserver.disconnect()
      this.headerResizeObserver = null
    }

    if (this.positionFrame !== null) {
      cancelAnimationFrame(this.positionFrame)
      this.positionFrame = null
    }

    this.setTriggerActive(false)
    this.setHeaderActive(false)
    this.setOverlayActive(false)
    this.removeOverlayElement()

    super.disconnectedCallback()
  }
}

if (!customElements.get('mega-menu')) {
  customElements.define('mega-menu', MegaMenu)
}
